/**
 * A faixa de abas de um espaço, na mesa e na janela própria do espaço (docs/spec/room-layout.md,
 * docs/spec/popout-windows.md).
 */
import { useEffect, useRef, type PointerEvent as RPointerEvent, type ReactNode } from 'react';

/**
 * As abas de um espaço: encolhem até um mínimo, com o nome cortado (inteiro na dica ao parar o
 * mouse em cima), e daí em diante rolam de lado, sem barra de rolagem, pela roda do mouse ou
 * pelo dedo; a ativa fica sempre à vista. O "+", a parte vazia da faixa, o Recolher e o ⋯ ficam
 * fora da lista. `vertical`: a faixa de pé do espaço recolhido, com as abas deitadas, que rola para
 * baixo.
 */
export function TabList({ active, label, vertical = false, children }: { active: string; label?: string; vertical?: boolean; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const list = ref.current;
    if (!list || vertical) return;
    const onWheel = (e: WheelEvent) => {
      if (list.scrollWidth <= list.clientWidth || Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
      e.preventDefault();
      list.scrollLeft += e.deltaMode === WheelEvent.DOM_DELTA_LINE ? e.deltaY * 16 : e.deltaY;
    };
    // o onWheel do React é passivo: sem o preventDefault, a página rolaria junto
    list.addEventListener('wheel', onWheel, { passive: false });
    return () => list.removeEventListener('wheel', onWheel);
  }, [vertical]);
  useEffect(() => {
    const list = ref.current;
    const tab = list?.querySelector('[aria-selected="true"]')?.closest('.rd-dock__tab');
    if (!list || !tab) return;
    const l = list.getBoundingClientRect();
    const r = tab.getBoundingClientRect();
    if (vertical) {
      if (r.top < l.top) list.scrollTop -= l.top - r.top;
      else if (r.bottom > l.bottom) list.scrollTop += r.bottom - l.bottom;
    } else if (r.left < l.left) list.scrollLeft -= l.left - r.left;
    else if (r.right > l.right) list.scrollLeft += r.right - l.right;
  }, [active, vertical]);
  return (
    <div ref={ref} className="rd-dock__tabList" role="tablist" aria-label={label} aria-orientation={vertical ? 'vertical' : undefined}>
      {children}
    </div>
  );
}

/** Dica com o nome inteiro, só quando ele não coube na aba (deitada, na faixa de pé, pela altura). */
export function fullTitle(e: RPointerEvent<HTMLButtonElement>) {
  const name = e.currentTarget.querySelector('.rd-dock__tabName');
  e.currentTarget.title = name && (name.scrollWidth > name.clientWidth || name.scrollHeight > name.clientHeight) ? (name.textContent ?? '') : '';
}
